import React, { useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  message,
  Popconfirm,
  Typography,
  Alert,
  Descriptions,
} from 'antd'
import { PlusOutlined, ReloadOutlined, ThunderboltOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { api } from '../services/api'

const { Title, Text } = Typography
const { Option } = Select

interface DynamicRiskRule {
  id: number
  name: string
  description?: string
  enabled: boolean
  condition_type: string
  condition_params: Record<string, any>
  action_type: string
  action_params: Record<string, any>
  poll_interval_seconds: number
}

interface RuleRuntimeState {
  rule_id: number
  rule_name: string
  condition_type: string
  action_type: string
  active: boolean
  triggered: boolean
  triggered_at?: string
  expires_at?: string
  last_eval_at?: string
  last_message?: string
  last_metric?: Record<string, any>
}

const DynamicRiskRules: React.FC = () => {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<DynamicRiskRule | null>(null)
  const [form] = Form.useForm()

  const { data: rules, isLoading } = useQuery<DynamicRiskRule[]>({
    queryKey: ['dynamic-risk-rules'],
    queryFn: () => api.dynamicRisk.listRules(),
  })

  const { data: status } = useQuery({
    queryKey: ['dynamic-risk-status'],
    queryFn: () => api.dynamicRisk.getStatus(),
    refetchInterval: 15000,
  })

  const { data: conditionTypes } = useQuery({
    queryKey: ['dynamic-risk-condition-types'],
    queryFn: () => api.dynamicRisk.getConditionTypes(),
  })

  const { data: actionTypes } = useQuery({
    queryKey: ['dynamic-risk-action-types'],
    queryFn: () => api.dynamicRisk.getActionTypes(),
  })

  const runtimeMap = React.useMemo(() => {
    const map = new Map<number, RuleRuntimeState>()
    for (const row of status?.rules || []) {
      map.set(row.rule_id, row)
    }
    return map
  }, [status])

  const saveMutation = useMutation({
    mutationFn: (payload: any) =>
      editingRule
        ? api.dynamicRisk.updateRule(editingRule.id, payload)
        : api.dynamicRisk.createRule(payload),
    onSuccess: () => {
      message.success(editingRule ? '规则已更新' : '规则已创建')
      setModalOpen(false)
      setEditingRule(null)
      form.resetFields()
      queryClient.invalidateQueries({ queryKey: ['dynamic-risk-rules'] })
      queryClient.invalidateQueries({ queryKey: ['dynamic-risk-status'] })
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || error?.message || '保存失败')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.dynamicRisk.deleteRule(id),
    onSuccess: () => {
      message.success('规则已删除')
      queryClient.invalidateQueries({ queryKey: ['dynamic-risk-rules'] })
      queryClient.invalidateQueries({ queryKey: ['dynamic-risk-status'] })
    },
  })

  const evaluateMutation = useMutation({
    mutationFn: () => api.dynamicRisk.evaluateNow(),
    onSuccess: () => {
      message.success('已立即评估所有规则')
      queryClient.invalidateQueries({ queryKey: ['dynamic-risk-status'] })
    },
  })

  const openCreate = () => {
    setEditingRule(null)
    form.setFieldsValue({
      enabled: true,
      condition_type: 'price_change_pct',
      action_type: 'half_first_order',
      poll_interval_seconds: 300,
      condition_params: {
        symbol: 'BTCUSDT',
        exchange: 'binance',
        window_hours: 1,
        threshold_pct: -2,
        direction: 'down',
      },
      action_params: {
        multiplier: 0.5,
        duration_hours: 24,
        scope: 'per_token_symbol',
      },
    })
    setModalOpen(true)
  }

  const openEdit = (record: DynamicRiskRule) => {
    setEditingRule(record)
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      enabled: record.enabled,
      condition_type: record.condition_type,
      action_type: record.action_type,
      poll_interval_seconds: record.poll_interval_seconds,
      condition_params: record.condition_params,
      action_params: record.action_params,
    })
    setModalOpen(true)
  }

  const handleConditionTypeChange = (value: string) => {
    const meta = (conditionTypes || []).find((x: any) => x.type === value)
    if (meta?.default_params) {
      form.setFieldValue('condition_params', meta.default_params)
    }
  }

  const handleActionTypeChange = (value: string) => {
    const meta = (actionTypes || []).find((x: any) => x.type === value)
    if (meta?.default_params) {
      form.setFieldValue('action_params', meta.default_params)
    }
  }

  const formatTime = (value?: string) => {
    if (!value) return '-'
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm:ss') : value
  }

  const activeCount = status?.active_rule_count ?? 0
  const triggeredCount = status?.triggered_rule_count ?? 0

  const renderEffectiveTag = (record: DynamicRiskRule) => {
    const rt = runtimeMap.get(record.id)
    if (!record.enabled) {
      return <Tag color="default">规则已禁用</Tag>
    }
    if (!rt?.last_eval_at) {
      return <Tag color="default">等待评估</Tag>
    }
    if (rt.active) {
      return (
        <Tag color="orange" icon={<CheckCircleOutlined />}>
          行为生效中
        </Tag>
      )
    }
    return (
      <Tag color="green" icon={<CloseCircleOutlined />}>
        行为未生效
      </Tag>
    )
  }

  const renderConditionTag = (record: DynamicRiskRule) => {
    const rt = runtimeMap.get(record.id)
    if (!rt?.last_eval_at) return <Tag>未评估</Tag>
    if (rt.triggered) {
      return <Tag color="red">条件成立</Tag>
    }
    return <Tag color="blue">条件未成立</Tag>
  }

  const columns: ColumnsType<DynamicRiskRule> = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
    {
      title: '条件',
      key: 'condition',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="blue">{record.condition_type}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.condition_params?.symbol || '-'} / {record.condition_params?.window_hours || '-'}h / {record.condition_params?.threshold_pct ?? '-'}%
          </Text>
        </Space>
      ),
    },
    {
      title: '行为',
      key: 'action',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Tag color="purple">{record.action_type}</Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            首单 x{record.action_params?.multiplier ?? 1} / {record.action_params?.duration_hours ?? '-'}h
          </Text>
        </Space>
      ),
    },
    {
      title: '轮询',
      dataIndex: 'poll_interval_seconds',
      key: 'poll_interval_seconds',
      width: 90,
      render: (v) => `${Math.round(Number(v) / 60)} 分钟`,
    },
    {
      title: '行为生效',
      key: 'effective',
      width: 120,
      render: (_, record) => renderEffectiveTag(record),
    },
    {
      title: '条件状态',
      key: 'condition_status',
      width: 110,
      render: (_, record) => renderConditionTag(record),
    },
    {
      title: '生效截止',
      key: 'expires_at',
      width: 170,
      render: (_, record) => {
        const rt = runtimeMap.get(record.id)
        if (!rt?.active) return '-'
        return formatTime(rt.expires_at)
      },
    },
    {
      title: '最近评估',
      key: 'last_eval_at',
      width: 170,
      render: (_, record) => formatTime(runtimeMap.get(record.id)?.last_eval_at),
    },
    {
      title: '条件详情',
      key: 'runtime',
      render: (_, record) => {
        const rt = runtimeMap.get(record.id)
        if (!rt?.last_message) return '-'
        return <Text type="secondary" style={{ fontSize: 12 }}>{rt.last_message}</Text>
      },
    },
    {
      title: '启用',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 80,
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'default'}>{enabled ? '是' : '否'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openEdit(record)}>编辑</Button>
          <Popconfirm title="确认删除该规则？" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button size="small" danger loading={deleteMutation.isPending}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleSubmit = async () => {
    const values = await form.validateFields()
    saveMutation.mutate({
      name: values.name,
      description: values.description,
      enabled: values.enabled,
      condition_type: values.condition_type,
      action_type: values.action_type,
      poll_interval_seconds: values.poll_interval_seconds,
      condition_params: values.condition_params,
      action_params: values.action_params,
    })
  }

  return (
    <div>
      <Title level={3}>动态风控规则</Title>
      <Text type="secondary">
        配置「条件 → 行为」规则。条件成立后在指定时间内生效；当前支持各 Token 各币种首单减半，后续信号正常。
      </Text>

      <Alert
        style={{ marginTop: 16 }}
        type={activeCount > 0 ? 'warning' : 'success'}
        showIcon
        message={
          activeCount > 0
            ? `当前有 ${activeCount} 条规则行为正在生效`
            : '当前无动态风控行为生效'
        }
        description={
          <div>
            <div>最近评估：{formatTime(status?.last_poll_at)}</div>
            <div>条件成立：{triggeredCount} 条 · 行为生效：{activeCount} 条</div>
            {activeCount === 0 && triggeredCount === 0 && (
              <div style={{ marginTop: 4 }}>
                行为生效 = 首单减仓正在对 Webhook 开仓信号起作用；条件未成立或已过生效期时显示「行为未生效」。
              </div>
            )}
          </div>
        }
      />

      {(status?.dashboard_alerts || []).length > 0 && (
        <Alert
          style={{ marginTop: 16 }}
          type="warning"
          showIcon
          message="当前动态风控状态"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              {(status.dashboard_alerts || []).map((item: string, idx: number) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          }
        />
      )}

      <Card style={{ marginTop: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建规则
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['dynamic-risk-rules'] })}>
            刷新
          </Button>
          <Button icon={<ThunderboltOutlined />} loading={evaluateMutation.isPending} onClick={() => evaluateMutation.mutate()}>
            立即评估
          </Button>
        </Space>

        <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
          <Descriptions.Item label="最近评估">{status?.last_poll_at || '-'}</Descriptions.Item>
          <Descriptions.Item label="生效规则数">{status?.active_rule_count ?? 0}</Descriptions.Item>
          <Descriptions.Item label="条件成立数">{status?.triggered_rule_count ?? 0}</Descriptions.Item>
        </Descriptions>

        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={rules || []}
          pagination={{ pageSize: 10 }}
          rowClassName={(record) => {
            const rt = runtimeMap.get(record.id)
            return rt?.active ? 'dynamic-risk-row-active' : ''
          }}
          scroll={{ x: 1300 }}
        />
      </Card>

      <Modal
        title={editingRule ? '编辑动态风控规则' : '新建动态风控规则'}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          setEditingRule(null)
        }}
        onOk={handleSubmit}
        confirmLoading={saveMutation.isPending}
        width={720}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="规则名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="例如：BTC 1小时跌超2% 首单减半" />
          </Form.Item>
          <Form.Item name="description" label="说明">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="poll_interval_seconds" label="评估间隔(秒)" rules={[{ required: true }]}>
            <InputNumber min={60} max={3600} step={60} style={{ width: '100%' }} />
          </Form.Item>

          <Title level={5}>条件</Title>
          <Form.Item name="condition_type" label="条件类型" rules={[{ required: true }]}>
            <Select onChange={handleConditionTypeChange}>
              {(conditionTypes || []).map((item: any) => (
                <Option key={item.type} value={item.type}>{item.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={['condition_params', 'symbol']} label="监视交易对" rules={[{ required: true }]}>
            <Input placeholder="BTCUSDT" />
          </Form.Item>
          <Form.Item name={['condition_params', 'exchange']} label="交易所" rules={[{ required: true }]}>
            <Select>
              <Option value="binance">binance</Option>
              <Option value="okx">okx</Option>
              <Option value="bybit">bybit</Option>
            </Select>
          </Form.Item>
          <Form.Item name={['condition_params', 'window_hours']} label="时间窗口(小时)" rules={[{ required: true }]}>
            <InputNumber min={1} max={24} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['condition_params', 'threshold_pct']} label="阈值(%)" rules={[{ required: true }]}>
            <InputNumber step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['condition_params', 'direction']} label="方向" rules={[{ required: true }]}>
            <Select>
              <Option value="down">下跌触发</Option>
              <Option value="up">上涨触发</Option>
              <Option value="abs">涨跌幅绝对值</Option>
            </Select>
          </Form.Item>

          <Title level={5}>行为</Title>
          <Form.Item name="action_type" label="行为类型" rules={[{ required: true }]}>
            <Select onChange={handleActionTypeChange}>
              {(actionTypes || []).map((item: any) => (
                <Option key={item.type} value={item.type}>{item.label}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name={['action_params', 'multiplier']} label="首单仓位乘数" rules={[{ required: true }]}>
            <InputNumber min={0.01} max={1} step={0.05} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name={['action_params', 'duration_hours']} label="生效时长(小时)" rules={[{ required: true }]}>
            <InputNumber min={1} max={168} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default DynamicRiskRules
